import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
} from 'react';
import type { RegistryApi } from '../api/registryApi';
import type { RegistryMe } from '../api/registryApiTypes';

/**
 * Permissions can change while the console is open (someone edits them in the admin portal).
 * Re-reads them when the tab comes back into view and when the server refuses a request, so the
 * screens follow without a reload. The server checks every request regardless.
 */
export function usePermissionRefresh(
  api: RegistryApi,
  refused: MutableRefObject<() => void>,
  setMe: Dispatch<SetStateAction<RegistryMe | null>>,
  enabled: boolean,
) {
  const refresh = useCallback(() => {
    api
      .getMe()
      .then((next) =>
        setMe((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next)),
      )
      .catch(() => undefined);
  }, [api, setMe]);

  useEffect(() => {
    refused.current = enabled ? refresh : () => undefined;
  }, [refused, refresh, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh, enabled]);
}
